import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Table } from '../components/ui/Table';
import { SearchFilter } from '../components/ui/SearchFilter';
import { Card } from '../components/ui/Card';
import { Download, RotateCcw } from 'lucide-react';
import { ordersReport, OrdersReportFilters } from '../services/api';

export const OrdersReport: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pagination, setPagination] = useState<{
    page: number;
    page_size: number;
    total: number;
  } | undefined>(undefined);

  // Get order ID from URL parameters
  const urlOrderId = searchParams.get('id');

  // Load orders from API
  const loadOrders = async () => {
    setLoading(true);
    try {
      // Use URL parameter if available, otherwise use search term
      const effectiveSearchTerm = urlOrderId || searchTerm;
      
      const apiFilters: OrdersReportFilters = {
        order_id: effectiveSearchTerm || undefined,
        user_email: effectiveSearchTerm || undefined,
        package_name: effectiveSearchTerm || undefined,
        payment_type: filters.paymentType?.length ? filters.paymentType as any : undefined,
        order_type: filters.orderType?.length ? filters.orderType as any : undefined,
        status: filters.status?.length ? filters.status as any : undefined,
      };
      
      // Use OR mode for general search (when searchTerm is provided and all fields have the same value)
      // Use AND mode for specific filters (when different filter values are provided)
      const hasGeneralSearch = effectiveSearchTerm && !filters.paymentType?.length && !filters.orderType?.length && !filters.status?.length;
      const searchMode = hasGeneralSearch ? 'OR' : 'AND';
      
      const result = await ordersReport(apiFilters, page, pageSize, searchMode);
      if (result && result.data) {
        setOrders(result.data);
        setPagination(result.pagination || undefined);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [page, pageSize, searchTerm, filters, urlOrderId]);

  // Handle page changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  // Reset all filters and clear URL parameters
  const handleResetFilters = () => {
    setSearchTerm('');
    setFilters({});
    setPage(1);
    // Clear URL parameters
    setSearchParams({});
  };

  // Filter groups for the search filter component
  const filterGroups = [{
    id: 'paymentType',
    label: 'Payment Type',
    options: [{
      id: 'Card',
      label: 'Card'
    }, {
      id: 'MaMs',
      label: 'MaMs'
    }, {
      id: 'Free',
      label: 'Free'
    }, {
      id: 'Support',
      label: 'Support'
    }]
  }, {
    id: 'orderType',
    label: 'Order Type',
    options: [{
      id: 'e-sim purchase',
      label: 'E-SIM Purchase'
    }, {
      id: 'Top-up',
      label: 'Top-up'
    }]
  }, {
    id: 'status',
    label: 'Status',
    options: [{
      id: 'Requested',
      label: 'Requested'
    }, {
      id: 'Pending',
      label: 'Pending'
    }, {
      id: 'Payment Pending',
      label: 'Payment Pending'
    }, {
      id: 'Active',
      label: 'Active'
    }, {
      id: 'Failed',
      label: 'Failed'
    }, {
      id: 'Canceled',
      label: 'Canceled'
    }]
  }];
  // Table columns
  const columns = [{
    key: 'name',
    header: 'Order ID'
  }, {
    key: 'order_id',
    header: 'Provider Order ID'
  }, {
    key: 'user_email',
    header: 'User'
  }, {
    key: 'package_name',
    header: 'Package'
  }, {
    key: 'price',
    header: 'Price (USD)',
    render: (value: number) => `$${value?.toFixed(2) || '0.00'}`
  }, {
    key: 'payment_type',
    header: 'Payment Type',
    render: (value: string, row: any) => {
      // Show "FREE" if is_free_esim is true, otherwise use payment_type value
      const displayValue = row.is_free_esim ? 'Free' : (value || '');
      let color = '';
      switch (displayValue) {
        case 'Card':
          color = 'bg-blue-100 text-blue-800';
          break;
        case 'MaMs':
          color = 'bg-purple-100 text-purple-800';
          break;
        case 'Free':
          color = 'bg-green-100 text-green-800';
          break;
        case 'Support':
          color = 'bg-yellow-100 text-yellow-800';
          break;
        default:
          color = 'bg-gray-100 text-gray-800';
      }
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
            {displayValue}
          </span>;
    }
  }, {
    key: 'order_type',
    header: 'Order Type'
  }, {
    key: 'date_created',
    header: 'Date Created'
  }, {
    key: 'date_activated',
    header: 'Date Activated'
  }, {
    key: 'status',
    header: 'Status',
    render: (value: string) => {
      let color = '';
      switch (value) {
        case 'successful':
          color = 'bg-green-100 text-green-800';
          break;
        case 'pending':
          color = 'bg-yellow-100 text-yellow-800';
          break;
        case 'failed':
          color = 'bg-red-100 text-red-800';
          break;
        default:
          color = 'bg-gray-100 text-gray-800';
      }
      return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
            {value}
          </span>;
    }
  }];
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Orders Report
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            View and filter all orders
          </p>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={handleResetFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset Filters
          </button>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
        </div>
      </div>
      <Card>
        <SearchFilter 
          placeholder="Search by Order ID (Provider or ESIM), User, or Package..." 
          filterGroups={filterGroups} 
          onSearch={setSearchTerm} 
          onFilter={setFilters}
          initialValue={urlOrderId || ''}
        />
        <Table 
          columns={columns} 
          data={orders} 
          isLoading={loading} 
          emptyMessage="No orders found matching your search criteria."
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Card>
    </div>;
};